import React from 'react';
import { Button, Group, Stack, Text, Paper, ThemeIcon, Box, Divider } from '@mantine/core';
import { Icon } from '@iconify/react';
import { useMachine } from '@xstate/react';
import { useAppState } from 'src/state';
import { useActions } from 'src/state';
import { createBookingStatusMachine } from 'src/machines/bookingStatus.machine';
import { TRANSITION_METADATA, hasPermission } from 'src/machines/bookingStatus.types';
import type { BookingStatusEvent, BookingStatusStateValue, GuardFailureCategory } from 'src/machines/bookingStatus.types';
import { notifications } from '@mantine/notifications';
import { Booking_Status_Enum } from 'src/state/gql/_types';
import { getTimelinePath, getTransitionDiagnostics } from 'src/machines/bookingStatus.diagnostics';
import { Icons } from '../ui/Icons';
import { BOOKING_STATUS_ENUM_PALETTE } from 'src/state/palettes';

const CATEGORY_ICONS: Record<GuardFailureCategory, string> = {
    permission: 'ph:lock-simple',
    validation: 'ph:warning',
    relationship: 'ph:users',
};

/**
 * Props for the BookingStatusMachine component
 */
export interface BookingStatusMachineProps {
    /**
     * Optional callback when a transition is completed
     */
    onTransition?: (newState: string) => void;

    /**
     * Optional spacing between buttons
     */
    spacing?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';

    /**
     * Optional size for buttons
     */
    buttonSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';

    /**
     * Show current status badge
     */
    showStatusBadge?: boolean;

    /**
     * Show button descriptions
     */
    showDescriptions?: boolean;

    /**
     * Show the booking progression timeline
     */
    showTimeline?: boolean;

    /**
     * Show guard failure diagnostics for blocked transitions
     */
    showDiagnostics?: boolean;
}

/**
 * BookingStatusMachine component
 *
 * Renders buttons for available booking status transitions based on XState machine
 * Guards ensure only valid transitions are shown based on user permissions and booking state
 */
export const BookingStatusMachine: React.FC<BookingStatusMachineProps> = ({
    onTransition,
    spacing = 'sm',
    buttonSize = 'sm',
    showTimeline = true,
    showDiagnostics = true,
}) => {

    // Get current user and booking from app state
    const currentUser = useAppState(state => state.profile);
    const { currentItem: currentBooking } = useAppState().bookings;

    // Get the update action
    const { updateItem } = useActions().bookings;

    // Create the state machine with the update function
    // Wrap updateItem to match the expected signature
    const wrappedUpdateItem = React.useCallback(
        async (params: { id: string; _set: { status: Booking_Status_Enum } }) => {
            const result = await updateItem(params);
            return result || null;
        },
        [updateItem]
    );

    // Create machine (recreated when booking changes to ensure proper hydration)
    const machine = React.useMemo(
        () => createBookingStatusMachine(wrappedUpdateItem),
        [wrappedUpdateItem, currentBooking?.id] // Include booking ID to trigger recreation
    );

    // Initialize the machine with input
    const [state, send] = useMachine(machine, {
        input: {
            currentUser,
            currentBooking,
        },
    });

    // Handle transition button click
    const handleTransition = React.useCallback(
        async (event: BookingStatusEvent['type']) => {
            try {
                send({ type: event });

                notifications.show({
                    title: 'Status Updated',
                    message: `Booking status updated successfully`,
                    color: 'green',
                });

                if (onTransition) {
                    onTransition(state.value as string);
                }
            } catch (error) {
                notifications.show({
                    title: 'Error',
                    message: `Failed to update booking status: ${error instanceof Error ? error.message : 'Unknown error'}`,
                    color: 'red',
                });
            }
        },
        [send, onTransition, state.value]
    );

    // If no booking is selected, show placeholder
    if (!currentBooking) {
        return (
            <Paper p="md" withBorder>
                <Text c="dimmed" size="sm">
                    No booking selected
                </Text>
            </Paper>
        );
    }

    // Compute timeline and diagnostics
    const currentStateValue = state.value as BookingStatusStateValue;
    const timelinePath = getTimelinePath(currentStateValue);
    const diagnostics = getTransitionDiagnostics(currentStateValue, { currentUser, currentBooking });
    const blockedTransitions = diagnostics.filter(d => {
        if (d.canTransition) return false;
        if (!currentUser) return false;
        return hasPermission(currentUser.role, currentUser.type, d.event);
    });

    // Get next events from diagnostics (correctly evaluates guard conditions)
    const nextEvents = diagnostics
        .filter(d => d.canTransition)
        .map(d => d.event);

    // Check if state is final (no more transitions available)
    const isFinalState = nextEvents.length === 0 && ['cancelled', 'cancellation', 'paid_out'].includes(state.value as string);

    // Order events: backward (cancel-type) first, then forward (progress-type)
    const backwardEvents = nextEvents.filter(
        (et) => TRANSITION_METADATA[et as BookingStatusEvent['type']]?.direction === 'backward'
    );
    const forwardEvents = nextEvents.filter(
        (et) => TRANSITION_METADATA[et as BookingStatusEvent['type']]?.direction === 'forward'
    );
    const orderedEvents = [...forwardEvents, ...backwardEvents];

    const renderActions = () => {
        return (
            <>

                <Box>

                    {showDiagnostics && blockedTransitions.length > 0 && (
                        <Stack gap="xs" mb="5">
                            {blockedTransitions.map((diag) => {
                                const metadata = TRANSITION_METADATA[diag.event as BookingStatusEvent['type']];
                                let deduplicatedFailures = diag.failures.filter((failure, index, self) => self.findIndex(f => f.message === failure.message) === index);
                                if (!metadata) return null;
                                return (
                                    <div key={diag.event}>
                                        <Paper
                                            p="xs"
                                            radius="md"
                                            shadow="0"
                                            withBorder
                                        >

                                            <Stack gap="8">

                                                <Group gap="8" align="flex-start" justify="space-between" wrap="nowrap">
                                                    <Box>
                                                        <Box fz="sm" fw={500} lh={1.2}>{metadata.label}</Box>
                                                        <Box fz="xs" c="dimmed" lh={1.2}>{metadata.description}</Box>
                                                    </Box>
                                                    <Button
                                                        variant={metadata.variant}
                                                        size={buttonSize}
                                                        disabled
                                                        color={metadata.color}
                                                        style={{
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        {metadata.cta}
                                                    </Button>
                                                </Group>

                                                <Stack gap="0">
                                                    {deduplicatedFailures.map((failure, i) => (
                                                        <Group
                                                            key={i}
                                                            gap="5"
                                                        >
                                                            <Icon
                                                                icon={CATEGORY_ICONS[failure.category]}
                                                                width={12}
                                                                style={{ verticalAlign: 'middle' }}
                                                                color="red"
                                                            />

                                                            <Text size="xs" c="red" fw={100}>
                                                                {failure.message}
                                                            </Text>
                                                        </Group>
                                                    ))}
                                                </Stack>

                                            </Stack>

                                        </Paper>

                                    </div>
                                )
                            })}
                        </Stack>
                    )}


                    {isFinalState ? (
                        <>
                            <Text c="dimmed" size="xs" ta="center">
                                This booking has reached a final state. No further transitions available.
                            </Text>
                        </>
                    ) : (
                        <Stack gap="3">

                            {orderedEvents.map((eventType) => {
                                const metadata = TRANSITION_METADATA[eventType as BookingStatusEvent['type']];
                                if (!metadata) return null;
                                return (
                                    <Paper
                                        key={eventType}
                                        p="xs"
                                        radius="md"
                                        shadow="0"
                                        withBorder
                                        style={{
                                            borderColor: `var(--mantine-color-${metadata.color}-4)`,
                                        }}
                                    >

                                        <Group gap="8" align="flex-start" justify="space-between" wrap="nowrap">

                                            <Group gap="8" align="flex-start" wrap="nowrap">
                                                <Box>
                                                    <Box fz="sm" fw={500} lh={1.2}>{metadata.label}</Box>
                                                    <Box fz="xs" c="dimmed" lh={1.2}>{metadata.description}</Box>
                                                </Box>
                                            </Group>

                                            <Button
                                                mt="auto"
                                                variant={metadata.variant}
                                                size={buttonSize}
                                                color={metadata.color}
                                                onClick={() => handleTransition(eventType as BookingStatusEvent['type'])}
                                            >
                                                {metadata.cta}
                                            </Button>

                                        </Group>

                                    </Paper>
                                );
                            })}

                        </Stack>
                    )}
                </Box>
            </>
        )
    }

    return (
        <Paper bg="transparent" p="0" shadow="0">

            <Stack>

                <Divider
                    my="0"
                    variant="dashed"
                    labelPosition="left"
                    color="var(--mantine-color-gray-4)"
                    styles={{
                        label: {
                            color: 'var(--mantine-color-gray-7)',
                            fontWeight: 400,
                            fontSize: '16px',
                        }
                    }}
                    label={
                        <Group gap="3" align="center" justify="flex-start">
                            <Icon icon={Icons.calendar} width={20} />
                            <Box ml={5}>Booking Status</Box>
                        </Group>
                    }
                />

                <Stack gap={spacing}>

                    {showTimeline && (
                        <Stack gap="5">

                            {timelinePath.steps.map((step) => {

                                const color = BOOKING_STATUS_ENUM_PALETTE[step.state];
                                const isCurrent = step.status === 'current';
                                const isPast = step.status === 'past';
                                const isFuture = step.status === 'future';

                                if (isFuture) return null;
                                if (isPast) return null;

                                return (
                                    <Paper
                                        key={step.state}
                                        id={step.state}
                                        p="8"
                                        radius="md"
                                        withBorder
                                        style={{
                                            borderWidth: isCurrent ? 2 : 1,
                                            borderColor: `var(--mantine-color-${color}-${isCurrent ? 6 : isPast ? 4 : 2})`,
                                            opacity: isFuture ? 0.55 : 1,
                                            backgroundColor: isCurrent
                                                ? `var(--mantine-color-${color}-0)`
                                                : undefined,
                                        }}
                                    >
                                        <Group gap="xs" justify="flex-start" align="center">
                                            <ThemeIcon
                                                size={32}
                                                radius="md"
                                                color={color}
                                                variant={isPast ? 'light' : isCurrent ? 'filled' : 'subtle'}
                                            >
                                                <Icon
                                                    icon={isPast ? 'ph:check-bold' : step.icon}
                                                    width={16}
                                                />
                                            </ThemeIcon>
                                            <Stack gap="0" justify="center">
                                                <Text
                                                    size="md"
                                                    fw={isCurrent ? 700 : 500}
                                                    c={isFuture ? 'dimmed' : undefined}
                                                    truncate
                                                    style={{
                                                        lineHeight: '1.1',
                                                    }}
                                                >
                                                    {step.label}
                                                </Text>
                                                {(isPast || isCurrent) && (
                                                    <Text size="xs" c="dimmed" lineClamp={2}>
                                                        {step.description}
                                                    </Text>
                                                )}

                                            </Stack>
                                        </Group>

                                    </Paper>
                                );
                            })}
                        </Stack>
                    )}

                </Stack>

                <Divider
                    my="0"
                    variant="dashed"
                    labelPosition="left"
                    color="var(--mantine-color-gray-4)"
                    styles={{
                        label: {
                            color: 'var(--mantine-color-gray-7)',
                            fontWeight: 400,
                            fontSize: '16px',
                        }
                    }}
                    label={
                        <Group gap="3" align="center" justify="flex-start">
                            <Icon icon={Icons.successCircle} width={20} />
                            <Box ml={5}>Available Actions</Box>
                        </Group>
                    }
                />

                {renderActions()}

            </Stack>

        </Paper>
    );
};

export default BookingStatusMachine;
